import { OralQuestionStatus } from './OralReport/OralReport.shared';

export enum PHQType {
  PHQ2 = 'PHQ-2',
  PHQ9 = 'PHQ-9'
}

export enum PHQResponseValue {
  NotAtAll = 0,
  SeveralDays = 1,
  MoreThanHalfTheDays = 2,
  NearlyEveryDay = 3
}

export enum DepressionScreenStep {
  Question1 = 'question1',
  Question2 = 'question2',
  Question3 = 'question3',
  Question4 = 'question4',
  Question5 = 'question5',
  Question6 = 'question6',
  Question7 = 'question7',
  Question8 = 'question8',
  Question9 = 'question9'
}

export interface DepressionScreeningAnswer {
  value: PHQResponseValue; // PHQ score for this question (0-3)
  status: OralQuestionStatus;
  additions: {
    score: number;
    outcome: null | number; // currently the only question with an outcome is the 9th question (suicidal ideation one)
    optionId: string;
    categoryId: string;
    questionId: string;
    optionTitle: string; // e.g., "Several days", "Not at all"
    questionTitle: string; // e.g., "Little interest or pleasure in doing things"
  };
  questionType: DepressionScreenStep;
}

export interface DepressionScreeningReport {
  answers: DepressionScreeningAnswer[];
}

export interface ProcessedDepressionData {
  phqType: PHQType;
  totalScore: number;
  hasSuicidalIdeation: boolean;
  suicidalIdeationResponse?: DepressionScreeningAnswer;
  responses: Array<{
    questionText: string;
    responseText: string;
    score: number;
    isSuicidalIdeationQuestion: boolean;
  }>;
}

// Helper function to determine if this is PHQ-2 or PHQ-9 based on number of answers
export const determinePHQType = (answers: DepressionScreeningAnswer[]): PHQType => {
  return answers.length <= 2 ? PHQType.PHQ2 : PHQType.PHQ9;
};

// Helper function to detect suicidal ideation (Question 9)
export const hasSuicidalIdeation = (answers: DepressionScreeningAnswer[]): boolean => {
  const q9Answer = getSuicidalIdeationResponse(answers);
  return q9Answer ? isSuicidalIdeationResponse(q9Answer.value) : false;
};

// Helper function to check if a specific response indicates suicidal ideation
export const isSuicidalIdeationResponse = (value: PHQResponseValue): boolean => {
  return value > PHQResponseValue.NotAtAll;
};

// Helper function to calculate total PHQ score
export const calculatePHQScore = (answers: DepressionScreeningAnswer[]): number => {
  return answers.reduce((total, answer) => total + answer.value, 0);
};

// Helper function to get suicidal ideation response
export const getSuicidalIdeationResponse = (
  answers: DepressionScreeningAnswer[]
): DepressionScreeningAnswer | undefined => {
  return answers.find((answer) => answer.questionType === DepressionScreenStep.Question9);
};
